import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useApi } from '../../context/ApiContext';
import { 
  FiChevronDown, 
  FiChevronRight, 
  FiChevronUp, 
  FiMenu, 
  FiX,
  FiHome
} from 'react-icons/fi';

const Navigation = () => {
  const api = useApi();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [openSubDropdown, setOpenSubDropdown] = useState(null);
  const [menus, setMenus] = useState([]);
  
  const location = useLocation();
  const navigate = useNavigate();
  const closeTimeout = useRef(null);

  // Fetch and nest menus from backend
  useEffect(() => {
    const fetchMenus = async () => {
      try {
        const data = await api.getMenus();
        
        // Helper to nest flat array into hierarchy
        const nest = (items, id = null) => {
          const targetIdStr = id ? (id.$oid || id.toString()) : null;
          
          return items
            .filter(item => {
              const parentIdStr = item.parent ? (item.parent.$oid || item.parent.toString()) : null;
              if (targetIdStr === null) return !parentIdStr;
              return parentIdStr === targetIdStr;
            })
            .map(item => ({
              ...item,
              subItems: nest(items, item._id)
            }))
            .sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
        };

        const nestedMenus = nest(data.filter(m => m.display));
        setMenus(nestedMenus);
      } catch (err) {
        console.error('❌ Error fetching menus:', err);
      }
    };

    fetchMenus();
  }, [api]);

  const handleLinkClick = (path) => {
    setOpenDropdown(null);
    setOpenSubDropdown(null);
    setMobileMenuOpen(false);
    navigate(path);
  };

  const handleMouseEnter = (setOpenFn, id) => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
      closeTimeout.current = null;
    }
    setOpenFn(id);
  };

  const handleMouseLeave = () => {
    closeTimeout.current = setTimeout(() => {
      setOpenDropdown(null);
      setOpenSubDropdown(null);
    }, 250);
  };

  // Recursive Mobile Menu Renderer
  const renderMobileItems = (items, level = 0) => (
    <ul className={level > 0 ? "pl-4 bg-black/5" : ""}>
      {items.map((item) => {
        const isParent = item.subItems?.length > 0;
        const isOpen = openDropdown === item._id || openSubDropdown === item._id;
        const path = item.page_type === 'url' ? item.external_link : `/page/${item.slug}`;

        return (
          <li key={item._id} className="border-b border-black/5 last:border-0">
            <div className="flex items-center justify-between">
              <Link
                to={isParent ? '#' : path}
                onClick={(e) => {
                  if (isParent) {
                    e.preventDefault();
                    if (level === 0) setOpenDropdown(isOpen ? null : item._id);
                    else setOpenSubDropdown(isOpen ? null : item._id);
                  } else {
                    handleLinkClick(path);
                  }
                }}
                className={`flex-grow py-3 px-4 text-sm font-medium uppercase hover:bg-black/5 transition-colors ${
                  location.pathname === path ? 'text-green-700 font-bold' : 'text-gray-700'
                }`}
              >
                {item.menu_name}
              </Link>
              {isParent && (
                <button 
                  onClick={() => {
                    if (level === 0) setOpenDropdown(isOpen ? null : item._id);
                    else setOpenSubDropdown(isOpen ? null : item._id);
                  }}
                  className="p-4 text-gray-500 hover:bg-black/5"
                >
                  {isOpen ? <FiChevronUp /> : <FiChevronDown />}
                </button>
              )}
            </div>
            <AnimatePresence>
              {isParent && isOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  {renderMobileItems(item.subItems, level + 1)}
                </motion.div>
              )}
            </AnimatePresence>
          </li>
        );
      })}
    </ul>
  );

  return (
    <nav className="w-full mt-2 bg-nav-green text-white sticky top-0 z-50 shadow-md font-sans">
      <div className="container mx-auto px-4">
        {/* Mobile Menu Toggle Row */}
        <div className="md:hidden flex items-center justify-between py-3">
          <Link to="/" onClick={() => handleLinkClick('/')} className="text-white">
            <FiHome size={22} />
          </Link>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="flex items-center gap-2 px-4 py-1.5 rounded border border-white/20 bg-white/5"
          >
            <span className="text-xs font-bold uppercase tracking-widest">Menu</span>
            {mobileMenuOpen ? <FiX size={20} /> : <FiMenu size={20} />}
          </button>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex flex-wrap items-center">
          <Link
            to="/"
            className="p-4 hover:bg-green-700 transition-colors border-r border-white/10"
            title="Home"
            onClick={() => handleLinkClick('/')}
          >
            <FiHome size={20} />
          </Link>

          {menus.map((item) => {
            const isParent = item.subItems?.length > 0;
            const path = item.page_type === 'url' ? item.external_link : `/page/${item.slug}`;

            return (
              <div
                key={item._id}
                className="relative"
                onMouseEnter={() => handleMouseEnter(setOpenDropdown, item._id)}
                onMouseLeave={handleMouseLeave}
              >
                <Link
                  to={path}
                  target={item.target || '_self'}
                  className={`px-5 py-4 flex items-center gap-1.5 text-[13px] font-bold uppercase tracking-wide transition-all border-r border-white/5 ${
                    openDropdown === item._id || location.pathname === path ? 'bg-green-700' : 'hover:bg-green-700/50'
                  }`}
                >
                  {item.menu_name}
                  {isParent && <FiChevronDown className="opacity-70" />}
                </Link>

                {/* Level 2 Dropdown (Sub Menu) */}
                <AnimatePresence>
                  {isParent && openDropdown === item._id && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.15 }}
                      className="absolute top-full left-0 bg-white text-gray-800 shadow-xl min-w-[250px] py-1 z-[60] border border-gray-100"
                    >
                      {item.subItems.map((subItem) => {
                        const hasChild = subItem.subItems?.length > 0;
                        const subPath = subItem.page_type === 'url' ? subItem.external_link : `/page/${subItem.slug}`;

                        return (
                          <div
                            key={subItem._id}
                            className="relative"
                            onMouseEnter={() => handleMouseEnter(setOpenSubDropdown, subItem._id)}
                          >
                            <Link
                              to={subPath}
                              target={subItem.target || '_self'}
                              className="flex items-center justify-between px-5 py-3 text-[14px] font-medium hover:bg-gray-50 hover:text-green-700 transition-colors border-b border-gray-50 last:border-0"
                              onClick={() => handleLinkClick(subPath)}
                            >
                              <span>{subItem.menu_name}</span>
                              {hasChild && <FiChevronRight className="text-gray-400" />}
                            </Link>

                            {/* Level 3 Dropdown (Child Menu) */}
                            <AnimatePresence>
                              {hasChild && openSubDropdown === subItem._id && (
                                <motion.div
                                  initial={{ opacity: 0, x: 5 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  exit={{ opacity: 0, x: 5 }}
                                  transition={{ duration: 0.15 }}
                                  className="absolute left-full top-0 ml-0.5 bg-white text-gray-800 shadow-xl min-w-[220px] py-1 z-[70] border border-gray-100"
                                >
                                  {subItem.subItems.map((childItem) => {
                                    const childPath = childItem.page_type === 'url' ? childItem.external_link : `/page/${childItem.slug}`;
                                    return (
                                      <Link
                                        key={childItem._id}
                                        to={childPath}
                                        target={childItem.target || '_self'}
                                        className="block px-5 py-3 text-[14px] font-medium hover:bg-gray-50 hover:text-green-700 transition-colors border-b border-gray-50 last:border-0"
                                        onClick={() => handleLinkClick(childPath)}
                                      >
                                        {childItem.menu_name}
                                      </Link>
                                    );
                                  })}
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        );
                      })}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>

        {/* Mobile Menu Content */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="md:hidden overflow-hidden bg-white text-gray-800 rounded-b-lg shadow-inner"
            >
              {renderMobileItems(menus)}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
};

export default Navigation;

